// src/pages/SegnalazioneClientePage.js
// Personale → Segnalazioni Cliente. Gestione strutturata di richieste/segnalazioni ricevute
// dai clienti (telefono, email, WhatsApp, colloquio, sopralluogo...) — NON una chat/messaggistica.
// Filosofia: un dato inserito una sola volta, riutilizzo di cliente/commessa già esistenti,
// nessuna doppia compilazione. L'utente vede qui esclusivamente le segnalazioni create da lui;
// la vista aggregata per tutti è nel Registro Segnalazioni Cliente (Gestione).
import React, { useState, useEffect, useMemo, useRef } from "react";
import { collection, query, where, onSnapshot, doc, addDoc, updateDoc, deleteDoc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { messaggioErrore } from "../utils/erroriUtente";
import { logModifica } from "../utils/logModifica";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, X, Trash2, Megaphone, Upload, FileText, Eye, Download, Clock } from "lucide-react";
import { chiaveBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza from "../components/AvvisoBozza";
import AvvisoViola from "../components/AvvisoViola";
import InsightButton from "../components/InsightButton";
import { clienteDellaCommessa, commesseDelCliente, etichettaCommessa, azioniSegnalazione, testoValido, daAvvisare, MIN_ESITO } from "../utils/segnalazioniCliente";

export const CANALI = ["Telefonata","E-mail","WhatsApp","Colloquio diretto","Sopralluogo","Altro"];
export const PRIORITA = ["Bassa","Media","Alta"];
export const STATI_SEG = {
  nuova:          { label:"Nuova",           bg:"#dbeafe", col:"#1d4ed8" },
  in_lavorazione: { label:"In lavorazione",  bg:"#fef3c7", col:"#92400e" },
  chiusa:         { label:"Chiusa",          bg:"#d1fae5", col:"#065f46" },
};

const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

// ── Form condiviso: creazione da Personale, modifica da Personale (proprie) o dal Registro ──
export function SegnalazioneForm({ seg, orgId, currentUser, utenti, clienti, commesseRecenti, canAdmin, onClose }) {
  const { addToast } = useToast();
  const [commesse, setCommesse] = useState([]);
  const [progettiPerId, setProgettiPerId] = useState({});   // anche le cartelle cliente, per risalire al cliente
  const [searchCommessa, setSearchCommessa] = useState("");
  const [nessunaCommessa, setNessunaCommessa] = useState(!!seg?.nessunaCommessa);
  const [azione, setAzione] = useState(null);               // null | "chiudi" | "riapri"
  const [testoAzione, setTestoAzione] = useState("");
  const [commessaSel, setCommessaSel] = useState(
    seg?.projectId ? { id:seg.projectId, name:seg.projectName, codiceCommessa:seg.codiceCommessa } : null
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    canale: seg?.canale || "Telefonata",
    clienteId: seg?.clienteId || "",
    clienteNome: seg?.clienteNome || "",
    oggetto: seg?.oggetto || "",
    descrizione: seg?.descrizione || "",
    priorita: seg?.priorita || "Media",
    stato: seg?.stato || "nuova",
    dataRicezione: seg?.dataRicezione || "",
    dataScadenza: seg?.dataScadenza || "",
    assegnatarioUid: seg?.assegnatarioUid || "",
    assegnatarioNome: seg?.assegnatarioNome || "",
  });
  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const [allegati, setAllegati] = useState(seg?.allegati || []);
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "segnalazione", orgId, seg?.id),
    valore: { form, allegati, commessa: commessaSel },
    info: { titolo: `${seg?.numero ? `Segnalazione ${seg.numero}` : "Nuova segnalazione cliente"}${form.oggetto ? ` — ${form.oggetto}` : ""}`,
      vai: { page: seg && canAdmin ? "registro-segnalazioni" : "segnalazioni-cliente" } },
    applica: d => {
      if (d.form) setForm(f => ({ ...f, ...d.form }));
      if (Array.isArray(d.allegati)) setAllegati(d.allegati);
      if (d.commessa !== undefined) setCommessaSel(d.commessa);
    },
  });
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();
  const [cronologia, setCronologia] = useState([]);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",orgId)),
      snap=>{
        const tutti = snap.docs.map(d=>({id:d.id,...d.data()}));
        setProgettiPerId(Object.fromEntries(tutti.map(p=>[p.id,p])));
        setCommesse(tutti.filter(p=>!p.archived && !p.isParent));
      });
  }, [orgId]);

  const commesseCliente = useMemo(() => commesseDelCliente(commesse, form, progettiPerId), [commesse, form, progettiPerId]);
  /** Scelta la commessa, il cliente è il suo (W409): niente segnalazioni con cliente e commessa che non tornano. */
  const scegliCommessa = (c) => {
    setCommessaSel(c); setSearchCommessa(""); setNessunaCommessa(false);
    const cl = clienteDellaCommessa(progettiPerId[c.id] || c, progettiPerId);
    const n = (x) => String(x || "").trim().toLowerCase();
    const inAnagrafica = clienti.find(x => (cl.clienteId && x.id === cl.clienteId) || (cl.clienteNome && n(x.ragioneSociale) === n(cl.clienteNome)));
    if (inAnagrafica) setForm(f => ({ ...f, clienteId: inAnagrafica.id, clienteNome: inAnagrafica.ragioneSociale || cl.clienteNome }));
    else if (cl.clienteNome) setForm(f => ({ ...f, clienteId: "", clienteNome: cl.clienteNome }));
  };
  const commesseFiltrate = commesse.filter(c=>
    !searchCommessa || c.name?.toLowerCase().includes(searchCommessa.toLowerCase()) || (c.codiceCommessa||"").toLowerCase().includes(searchCommessa.toLowerCase())
  ).sort((a,b)=>a.name?.localeCompare(b.name||"","it"));

  // Cronologia delle azioni — stesso audit trail (auditLog) già usato in Centro Operativo,
  // filtrato per questa segnalazione. Nessuna nuova collection, riuso di logModifica.
  useEffect(() => {
    if (!seg?.id) { setCronologia([]); return; }
    return onSnapshot(
      query(collection(db,"auditLog"), where("sezione","==","segnalazioni"), where("refId","==",seg.id), where("orgId","==",orgId)),
      snap => setCronologia(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.createdAt?.seconds||0)-(b.createdAt?.seconds||0)))
    );
  }, [seg?.id, orgId]);

  const uploadAllegato = async (file) => {
    setUploading(true);
    try {
      const estensione = file.name.split(".").pop().toLowerCase();
      const path = `segnalazioni-cliente/${orgId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const tipo = ["jpg","jpeg","png","webp","heic"].includes(estensione) ? "foto" : "pdf";
      setAllegati(a => [...a, { url, nome: file.name, tipo, path }]);
    } catch (err) {
      addToast(messaggioErrore(err, "caricare l'allegato"), "error");
    }
    setUploading(false);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) await uploadAllegato(file);
    e.target.value = "";
  };

  const removeAllegato = (i) => setAllegati(a => a.filter((_,j)=>j!==i));

  // Permessi: chi ha creato la segnalazione o un admin può modificarne il contenuto —
  // ma SOLO finché nessuno l'ha ancora presa in carico. Una volta che l'assegnatario la
  // prende in carico esplicitamente, il contenuto originale si blocca per tutti (compilatore
  // e admin inclusi): l'assegnatario deve poter fidarsi di ciò che ha letto e su cui sta lavorando.
  const isCompilatore = !seg || seg.compilatoreUid === currentUser?.uid;
  const isAssegnatarioAttuale = seg && seg.assegnatarioUid === currentUser?.uid;
  const contenutoBloccato = !!seg?.presaInCaricoData;
  const puoModificareContenuto = (isCompilatore || canAdmin) && !contenutoBloccato;
  const puoGestire = isCompilatore || isAssegnatarioAttuale || canAdmin;
  // La Data scadenza la imposta solo chi prende in carico (l'assegnatario attuale), o un admin —
  // mai automaticamente chi crea la segnalazione per conto di un collega.
  const puoImpostareScadenza = isAssegnatarioAttuale || canAdmin || (isCompilatore && (form.assegnatarioUid===currentUser?.uid || !form.assegnatarioUid));

  // ── Ciclo a pulsanti (W409): presa in carico → chiusura con esito → eventuale riapertura ──
  const azioni = azioniSegnalazione(seg, currentUser?.uid, { canAdmin });
  const chi = currentUser?.displayName || "Un collega";
  const etichetta = `${seg?.numero || ""}${seg?.clienteNome ? ` · ${seg.clienteNome}` : ""}${seg?.codiceCommessa ? ` · ${seg.codiceCommessa}` : ""}`;
  // presa in carico, chiusura e riapertura: solo in campanella, mai email (W415). L'email resta
  // per la segnalazione nuova o assegnata (tipo «segnalazione_cliente», functions/index.js).
  const avvisa = async (titolo, testo, segAgg = seg) => {
    for (const uid of new Set([...daAvvisare(seg, currentUser?.uid), ...daAvvisare(segAgg, currentUser?.uid)])) {
      await addDoc(collection(db,"notifiche"), { userId: uid, orgId, tipo:"segnalazione_cliente_stato", titolo, testo,
        projectId: seg.projectId || null, rifCollezione:"segnalazioniCliente", rifId: seg.id, letta:false, createdAt:serverTimestamp() });
    }
  };
  const registra = (campo, campoLabel, before, after) => logModifica({ orgId, uid: currentUser.uid, nomeUtente: chi,
    sezione: "segnalazioni", refId: seg.id, refNome: seg.numero || seg.oggetto || "", campo, campoLabel, before, after });

  const prendiInCarico = async () => {
    if (!seg?.id || !azioni.prendi) return;
    const agg = { presaInCaricoUid: currentUser.uid, presaInCaricoNome: chi, presaInCaricoData: serverTimestamp(), stato: "in_lavorazione", updatedAt: serverTimestamp() };
    // senza assegnatario, chi la prende diventa l'assegnatario
    if (!seg.assegnatarioUid || canAdmin) Object.assign(agg, { assegnatarioUid: currentUser.uid, assegnatarioNome: chi });
    try {
      await updateDoc(doc(db,"segnalazioniCliente",seg.id), agg);
      await registra("presaInCarico", "Presa in carico", "—", chi);
      await avvisa("Segnalazione presa in carico", `${chi} ha preso in carico la segnalazione ${etichetta}`, { ...seg, ...agg });
      addToast("Presa in carico ✅ — il contenuto originale ora è bloccato","success");
      onClose();
    } catch (err) { addToast(messaggioErrore(err), "error"); }
  };

  const confermaAzione = async () => {
    if (!testoValido(testoAzione)) { addToast(azione === "chiudi" ? "Scrivi l'esito: cosa è stato fatto o deciso" : "Scrivi perché la riapri", "error"); return; }
    const testo = testoAzione.trim();
    try {
      if (azione === "chiudi") {
        await updateDoc(doc(db,"segnalazioniCliente",seg.id), { stato: "chiusa", esito: testo, chiusaDaUid: currentUser.uid, chiusaDaNome: chi, chiusaData: serverTimestamp(), updatedAt: serverTimestamp() });
        await registra("chiusura", "Chiusura", STATI_SEG[seg.stato]?.label || seg.stato, testo);
        await avvisa("Segnalazione chiusa", `${chi} ha chiuso la segnalazione ${etichetta}. Esito: ${testo.slice(0, 160)}`);
        addToast("Segnalazione chiusa ✅","success");
      } else {
        const voce = { motivo: testo, daUid: currentUser.uid, daNome: chi, il: new Date().toISOString(), esitoPrecedente: seg.esito || "" };
        await updateDoc(doc(db,"segnalazioniCliente",seg.id), { stato: seg.presaInCaricoData ? "in_lavorazione" : "nuova", esito: "", chiusaDaUid: "", chiusaDaNome: "", chiusaData: null,
          riaperture: [...(seg.riaperture || []), voce], updatedAt: serverTimestamp() });
        await registra("riapertura", "Riapertura", "Chiusa", testo);
        await avvisa("Segnalazione riaperta", `${chi} ha riaperto la segnalazione ${etichetta}: ${testo.slice(0, 160)}`);
        addToast("Segnalazione riaperta","success");
      }
      onClose();
    } catch (err) { addToast(messaggioErrore(err), "error"); }
  };

  const stampaPDF = () => {
    if (!seg?.id) return;
    const win = window.open("","_blank");
    const stato = STATI_SEG[seg.stato]||STATI_SEG.nuova;
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Segnalazione ${seg.numero}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:15mm 18mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;}
      @media print{.no-print{display:none;}}
      .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e1b4b;padding-bottom:12px;margin-bottom:16px;}
      .numero{font-size:20px;font-weight:900;color:#7c3aed;font-family:monospace;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px;}
      .campo{margin-bottom:10px;}
      .lbl{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.04em;margin-bottom:2px;}
      .val{font-size:13px;color:#111827;}
      .desc{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;margin-bottom:16px;font-size:13px;line-height:1.5;}
    </style></head><body>
      <div class="hdr">
        <div>
          <div class="numero">${seg.numero}</div>
          <div style="font-size:16px;font-weight:800;color:#1e1b4b;margin-top:4px;">📣 Segnalazione Cliente</div>
        </div>
        <div style="text-align:right;font-size:11px;color:#6b7280;">
          Stato: <strong style="color:#111827;">${stato.label}</strong><br/>
          Generato il ${new Date().toLocaleDateString("it-IT")}
        </div>
      </div>
      <div class="grid">
        <div class="campo"><div class="lbl">Cliente</div><div class="val">${seg.clienteNome||"—"}</div></div>
        <div class="campo"><div class="lbl">Commessa</div><div class="val">${etichettaCommessa(seg)||(seg.nessunaCommessa?"Non riguarda una commessa":"—")}</div></div>
        <div class="campo"><div class="lbl">Canale</div><div class="val">${seg.canale||"—"}</div></div>
        <div class="campo"><div class="lbl">Priorità</div><div class="val">${seg.priorita||"—"}</div></div>
        <div class="campo"><div class="lbl">Data ricezione</div><div class="val">${seg.dataRicezione?.split("-").reverse().join("/")||"—"}</div></div>
        <div class="campo"><div class="lbl">Data scadenza</div><div class="val">${seg.dataScadenza?.split("-").reverse().join("/")||"—"}</div></div>
        <div class="campo"><div class="lbl">Assegnata a</div><div class="val">${seg.assegnatarioNome||"—"}</div></div>
        <div class="campo"><div class="lbl">Compilata da</div><div class="val">${seg.compilatoreNome||"—"}</div></div>
      </div>
      <div class="lbl">Oggetto</div>
      <div class="desc" style="margin-bottom:10px;font-weight:700;">${seg.oggetto||"—"}</div>
      <div class="lbl">Descrizione della richiesta</div>
      <div class="desc">${(seg.descrizione||"—").replace(/\n/g,"<br/>")}</div>
      ${seg.presaInCaricoData ? `<div style="font-size:11px;color:#7c3aed;">✋ Presa in carico da ${seg.presaInCaricoNome}</div>` : ""}
      ${seg.stato==="chiusa" && seg.esito ? `<div class="lbl" style="margin-top:14px;">Esito (chiusa da ${seg.chiusaDaNome||"—"})</div><div class="desc">${String(seg.esito).replace(/</g,"&lt;").replace(/\n/g,"<br/>")}</div>` : ""}
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  const salva = async () => {
    if (!form.oggetto.trim()) { addToast("Indica l'oggetto della segnalazione","error"); return; }
    if (!form.descrizione.trim()) { addToast("Descrivi la richiesta del cliente","error"); return; }
    if (!form.clienteId && !form.clienteNome.trim()) { addToast("Indica il cliente (esistente o nome libero)","error"); return; }
    if (!form.dataRicezione) { addToast("Indica la data di ricezione","error"); return; }
    // Su quale commessa? Se il cliente ne ha, va scelta, o detto che non ne riguarda nessuna (W409)
    if (!commessaSel && !nessunaCommessa && commesseCliente.length > 0) { addToast("Scegli la commessa del cliente, oppure «Non riguarda una commessa»","error"); return; }
    setSaving(true);
    try {
      const dati = {
        canale: form.canale,
        clienteId: form.clienteId, clienteNome: form.clienteNome,
        projectId: commessaSel?.id || "", projectName: commessaSel?.name || "", codiceCommessa: commessaSel?.codiceCommessa || "",
        oggetto: form.oggetto,
        descrizione: form.descrizione,
        priorita: form.priorita,
        nessunaCommessa: !commessaSel && nessunaCommessa,
        dataRicezione: form.dataRicezione,
        dataScadenza: form.dataScadenza,
        assegnatarioUid: form.assegnatarioUid, assegnatarioNome: form.assegnatarioNome,
        allegati,
        orgId, updatedAt: serverTimestamp(),
      };
      let numeroFinale = seg?.numero;
      if (seg?.id) {
        // Traccia visibile di chi ha modificato per ultimo e quando — così chi riapre la
        // segnalazione vede subito se non è più nella forma in cui l'ha letta la prima volta.
        dati.ultimaModificaUid = currentUser?.uid;
        dati.ultimaModificaNome = currentUser?.displayName || "";
        dati.ultimaModificaData = serverTimestamp();
        await updateDoc(doc(db,"segnalazioniCliente",seg.id), dati);
        // Cronologia — solo i cambiamenti che contano davvero per ricostruire la storia
        // della segnalazione: stato e assegnatario (logModifica ignora da sé i "non cambi").
        await logModifica({
          orgId, uid: currentUser?.uid, nomeUtente: currentUser?.displayName || "",
          sezione: "segnalazioni", refId: seg.id, refNome: numeroFinale || form.oggetto,
          campo: "assegnatarioNome", campoLabel: "Assegnatario",
          before: seg.assegnatarioNome || "Nessuno", after: form.assegnatarioNome || "Nessuno",
        });
        if (form.assegnatarioUid && form.assegnatarioUid !== seg.assegnatarioUid) {
          await addDoc(collection(db,"notifiche"), {
            userId: form.assegnatarioUid, orgId, tipo:"segnalazione_cliente",
            titolo:"Segnalazione cliente assegnata",
            testo:`${currentUser?.displayName||"Un collega"} ti ha assegnato la segnalazione ${seg.numero} (${form.clienteNome||"—"}${commessaSel?.codiceCommessa?` · ${commessaSel.codiceCommessa}`:""})`, rifCollezione:"segnalazioniCliente", rifId: seg.id,
            projectId: commessaSel?.id||null, letta:false, createdAt:serverTimestamp(),
          });
        } else if (form.assegnatarioUid && currentUser?.uid !== form.assegnatarioUid) {
          // Stesso assegnatario di prima, ma a modificare è qualcun altro — l'assegnatario
          // deve saperlo, anche se non ha ancora preso in carico ufficialmente la segnalazione.
          await addDoc(collection(db,"notifiche"), {
            userId: form.assegnatarioUid, orgId, tipo:"segnalazione_cliente",
            titolo:"Segnalazione cliente modificata",
            testo:`${currentUser?.displayName||"Un collega"} ha modificato la segnalazione ${seg.numero} (${form.clienteNome||"—"}${commessaSel?.codiceCommessa?` · ${commessaSel.codiceCommessa}`:""}) che hai in carico`, rifCollezione:"segnalazioniCliente", rifId: seg.id,
            projectId: commessaSel?.id||null, letta:false, createdAt:serverTimestamp(),
          });
        }
        addToast("Segnalazione aggiornata ✅","success");
      } else {
        const anno = new Date().getFullYear();
        const prog = await prossimoProgressivo(`${orgId}_seg_${anno}`, { orgId, anno });
        numeroFinale = `SEG-${anno}-${String(prog).padStart(4,"0")}`;
        const nuovoRef = await addDoc(collection(db,"segnalazioniCliente"), {
          ...dati, numero:numeroFinale, stato:"nuova",
          compilatoreUid: currentUser?.uid, compilatoreNome: currentUser?.displayName||"",
          trasformataIn: null, // predisposizione per evoluzione futura (Attività/Offerta/Commessa/Manutenzione) — non usato ora
          createdAt: serverTimestamp(),
        });
        await logModifica({
          orgId, uid: currentUser?.uid, nomeUtente: currentUser?.displayName || "",
          sezione: "segnalazioni", refId: nuovoRef.id, refNome: numeroFinale,
          campo: "creazione", campoLabel: "Segnalazione creata",
          before: "—", after: form.clienteNome || "—",
        });
        if (form.assegnatarioUid && form.assegnatarioUid !== currentUser?.uid) {
          await addDoc(collection(db,"notifiche"), {
            userId: form.assegnatarioUid, orgId, tipo:"segnalazione_cliente",
            titolo:"Nuova segnalazione cliente",
            testo:`${currentUser?.displayName||"Un collega"} ti ha assegnato la segnalazione ${numeroFinale} (${form.clienteNome||"—"}${commessaSel?.codiceCommessa?` · ${commessaSel.codiceCommessa}`:""})`, rifCollezione:"segnalazioniCliente", rifId: nuovoRef.id,
            projectId: commessaSel?.id||null, letta:false, createdAt:serverTimestamp(),
          });
        }
        addToast(`Segnalazione ${numeroFinale} creata ✅`,"success");
      }
      setSaved(true);
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:640,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            <Megaphone size={16} color="#7c3aed"/> {seg ? `Segnalazione ${seg.numero}` : "Nuova segnalazione cliente"}
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        {bozza.trovata && <AvvisoBozza testo={seg ? "Hai modifiche non salvate a questa segnalazione" : "Hai una segnalazione iniziata e non salvata"}
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

        {!seg && (
          <AvvisoViola style={{marginBottom:12}}>
            Per guasti, problemi dell'impianto e richieste d'intervento dopo la consegna. Se la commessa è ancora in esecuzione, quello che il cliente segnala sui lavori va registrato in <b>Corrispondenza</b> dentro la commessa.
          </AvvisoViola>
        )}

        {seg?.presaInCaricoData && (
          <div style={{fontSize:11,color:"#7c3aed",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"7px 10px",marginBottom:10}}>
            ✋ Presa in carico da <strong>{seg.presaInCaricoNome}</strong> — il contenuto originale è bloccato, non più modificabile.
          </div>
        )}
        {seg?.ultimaModificaData && (
          <div style={{fontSize:11,color:"#9ca3af",marginBottom:10}}>
            Ultima modifica: {seg.ultimaModificaNome}{seg.ultimaModificaData?.toDate && `, ${seg.ultimaModificaData.toDate().toLocaleDateString("it-IT")} ${seg.ultimaModificaData.toDate().toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}`}
          </div>
        )}

        <fieldset disabled={!puoGestire || seg?.stato==="chiusa"} style={{border:"none",padding:0,margin:0}}>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={lbl}>Canale di ricezione</label>
            <select style={inp} value={form.canale} onChange={e=>upd("canale",e.target.value)} disabled={!puoModificareContenuto}>
              {CANALI.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Data ricezione *</label>
            <input type="date" style={inp} value={form.dataRicezione} onChange={e=>upd("dataRicezione",e.target.value)} disabled={!puoModificareContenuto}/>
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Cliente *</label>
          <select style={inp} value={form.clienteId} onChange={e=>{
            const cl = clienti.find(c=>c.id===e.target.value);
            upd("clienteId",e.target.value); upd("clienteNome",cl?.ragioneSociale||"");
            // cambiato cliente: la commessa scelta prima non è più la sua
            if (commessaSel) { const di = clienteDellaCommessa(progettiPerId[commessaSel.id] || commessaSel, progettiPerId);
              if (di.clienteId !== e.target.value && String(di.clienteNome||"").toLowerCase() !== String(cl?.ragioneSociale||"").toLowerCase()) setCommessaSel(null); }
          }} disabled={!puoModificareContenuto}>
            <option value="">— Seleziona cliente —</option>
            {clienti.map(c=><option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
          </select>
          {!form.clienteId && (
            <input style={{...inp,marginTop:6}} value={form.clienteNome} onChange={e=>upd("clienteNome",e.target.value)}
              placeholder="O scrivi il nome del cliente/potenziale cliente..." disabled={!puoModificareContenuto}/>
          )}
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Commessa{commesseCliente.length > 0 ? " *" : ""}</label>
          {commessaSel ? (
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:"#ede9fe",border:"2px solid #7c3aed",borderRadius:8}}>
              <span style={{flex:1,fontSize:13,fontWeight:700,color:"#7c3aed"}}>
                ✓ {commessaSel.codiceCommessa && `[${commessaSel.codiceCommessa}] `}{commessaSel.name}
              </span>
              {puoModificareContenuto && (
                <button type="button" onClick={()=>{setCommessaSel(null);setSearchCommessa("");}} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:0}}><X size={14}/></button>
              )}
            </div>
          ) : puoModificareContenuto ? (
            <div>
              {commesseCliente.length > 0 && (
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:6}}>Commesse di {form.clienteNome}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {commesseCliente.slice(0,10).map(c=>(
                      <button key={c.id} type="button" onClick={()=>scegliCommessa(c)}
                        style={{padding:"6px 12px",borderRadius:20,border:"1.5px solid #c4b5fd",background:"#f5f3ff",color:"#5b21b6",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        {c.codiceCommessa && `[${c.codiceCommessa}] `}{c.name}{c.statoCommessa==="chiusa" ? " · chiusa" : ""}
                      </button>
                    ))}
                    <button type="button" onClick={()=>setNessunaCommessa(v=>!v)} aria-pressed={nessunaCommessa}
                      style={{padding:"6px 12px",borderRadius:20,border:`1.5px solid ${nessunaCommessa?"#6b7280":"#e5e7eb"}`,background:nessunaCommessa?"#f3f4f6":"#fff",color:"#374151",fontSize:12,fontWeight:nessunaCommessa?700:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                      {nessunaCommessa ? "✓ " : ""}Non riguarda una commessa
                    </button>
                  </div>
                </div>
              )}
              {commesseCliente.length === 0 && commesseRecenti && commesseRecenti.length>0 && (
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:600,color:"#9ca3af",marginBottom:6}}>Commesse recenti</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {commesseRecenti.map(c=>(
                      <button key={c.id} type="button" onClick={()=>scegliCommessa(c)}
                        style={{padding:"6px 12px",borderRadius:20,border:"1px solid #e9d5ff",background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        {c.codiceCommessa && `[${c.codiceCommessa}] `}{c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <input style={inp} value={searchCommessa} onChange={e=>setSearchCommessa(e.target.value)} placeholder="🔍 Cerca per numero o nome commessa..."/>
              {searchCommessa && (
                <div style={{border:"1px solid #e5e7eb",borderRadius:8,maxHeight:160,overflowY:"auto",background:"#fff",marginTop:6}}>
                  {commesseFiltrate.length===0 ? (
                    <div style={{padding:"12px 14px",color:"#9ca3af",fontSize:13}}>Nessuna commessa trovata</div>
                  ) : commesseFiltrate.map(c=>(
                    <div key={c.id} onClick={()=>scegliCommessa(c)}
                      style={{padding:"9px 14px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f3f4f6",fontWeight:500}}
                      onMouseOver={e=>e.currentTarget.style.background="#f9f5ff"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                      {c.codiceCommessa && <span style={{color:"#7c3aed",fontFamily:"monospace",marginRight:6}}>[{c.codiceCommessa}]</span>}{c.name}
                    </div>
                  ))}
                </div>
              )}
              <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>{commesseCliente.length > 0
                ? "Tocca la commessa a cui si riferisce la segnalazione; se è una richiesta nuova (es. un'offerta) scegli «Non riguarda una commessa»."
                : "Scegliendo la commessa, il cliente si compila da solo. Se la richiesta non riguarda ancora una commessa (es. nuova offerta), lascia vuoto: resterà collegata solo al cliente."}</div>
            </div>
          ) : <div style={{fontSize:13,color:"#9ca3af"}}>{seg?.nessunaCommessa ? "Non riguarda una commessa" : "— Nessuna —"}</div>}
          {commessaSel && commessaSel.statoCommessa !== "chiusa" && !seg && (
            <AvvisoViola compatto style={{marginTop:6}}>
              Questa commessa è ancora in esecuzione: se il cliente segnala qualcosa sui lavori in corso, registralo in <b>Corrispondenza</b> della commessa. Usa questa segnalazione solo per un guasto o una richiesta d'intervento su un impianto già consegnato.
            </AvvisoViola>
          )}
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Oggetto *</label>
          <input style={inp} value={form.oggetto} onChange={e=>upd("oggetto",e.target.value)}
            placeholder="Riassunto breve della richiesta (es. Perdita quadro elettrico piano 2)" disabled={!puoModificareContenuto}/>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Descrizione della richiesta *</label>
          <textarea style={{...inp,minHeight:80,resize:"vertical"}} value={form.descrizione} onChange={e=>upd("descrizione",e.target.value)}
            placeholder="Cosa ha chiesto/segnalato il cliente..." disabled={!puoModificareContenuto}/>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={lbl}>Priorità</label>
            <div style={{display:"flex",gap:6}}>
              {PRIORITA.map(p=>(
                <button key={p} type="button" onClick={()=>upd("priorita",p)}
                  style={{flex:1,padding:"7px 0",border:`2px solid ${form.priorita===p?(p==="Alta"?"#dc2626":"#7c3aed"):"#e5e7eb"}`,
                    borderRadius:8,background:form.priorita===p?(p==="Alta"?"#fef2f2":"#faf5ff"):"#fff",
                    color:form.priorita===p?(p==="Alta"?"#dc2626":"#7c3aed"):"#6b7280",fontWeight:form.priorita===p?700:400,
                    cursor:"pointer",fontFamily:"'Inter',sans-serif",fontSize:12}}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Stato</label>
            {(() => { const st = STATI_SEG[seg?.stato || "nuova"] || STATI_SEG.nuova; return (
              <div style={{display:"flex",alignItems:"center",gap:8,minHeight:34}}>
                <span style={{fontSize:12,padding:"5px 12px",borderRadius:10,background:st.bg,color:st.col,fontWeight:800}}>{st.label}</span>
                <span style={{fontSize:10.5,color:"#9ca3af",lineHeight:1.3}}>{!seg ? "si cambia con i pulsanti in fondo" : seg.stato==="chiusa" ? "chiusa con esito" : "si cambia con i pulsanti in fondo"}</span>
              </div>); })()}
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={{...lbl,color:"#d97706",fontWeight:700}}>📤 ASSEGNA A</label>
          <select style={{...inp,borderColor:form.assegnatarioUid?"#d97706":"#e5e7eb"}} value={form.assegnatarioUid} onChange={e=>{
            const u = utenti.find(x=>x.uid===e.target.value);
            upd("assegnatarioUid",e.target.value); upd("assegnatarioNome",u?.displayName||"");
          }}>
            <option value="">— Nessuno —</option>
            {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
          </select>
        </div>

        <div style={{marginBottom:8}}>
          <label style={lbl}>Data scadenza</label>
          <input type="date" style={{...inp, background: puoImpostareScadenza?"#fff":"#f9fafb", color: puoImpostareScadenza?"#111827":"#9ca3af"}}
            value={form.dataScadenza} onChange={e=>upd("dataScadenza",e.target.value)} disabled={!puoImpostareScadenza}/>
          <div style={{fontSize:11,color:"#7c3aed",marginTop:5}}>
            💜 Nexiora – La Data scadenza dovrà essere inserita dalla persona che prenderà in carico la Segnalazione, dopo averne valutato priorità e tempi di gestione.
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={lbl}>Allegati / Fotografie</label>
          {puoModificareContenuto && (
            <div onClick={()=>fileRef.current?.click()}
              style={{border:"2px dashed #7c3aed",borderRadius:8,padding:"14px 12px",textAlign:"center",cursor:"pointer",background:"#faf5ff",marginBottom:allegati.length>0?10:0}}>
              <Upload size={20} color="#7c3aed" style={{marginBottom:4}}/>
              <div style={{fontSize:12,fontWeight:700,color:"#7c3aed"}}>{uploading?"Caricamento in corso...":"Carica foto o PDF"}</div>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Puoi selezionare più file — JPG, PNG, HEIC, PDF</div>
            </div>
          )}
          <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.heic" style={{display:"none"}} onChange={handleFileChange}/>
          {allegati.length>0 && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(110px, 1fr))",gap:8}}>
              {allegati.map((a,i)=>(
                <div key={i} style={{border:"1px solid #e5e7eb",borderRadius:8,overflow:"hidden",background:"#fff"}}>
                  {a.tipo==="foto" ? (
                    <img src={a.url} alt={a.nome} style={{width:"100%",height:80,objectFit:"cover",cursor:"pointer"}} onClick={()=>setPreview(a)}/>
                  ) : (
                    <div style={{height:80,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"#fef2f2",cursor:"pointer"}} onClick={()=>setPreview(a)}>
                      <FileText size={22} color="#dc2626"/>
                      <div style={{fontSize:9,color:"#dc2626",marginTop:2,fontWeight:600}}>PDF</div>
                    </div>
                  )}
                  <div style={{padding:"4px 6px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontSize:9,color:"#6b7280",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{a.nome}</span>
                    {puoModificareContenuto && (
                      <button type="button" onClick={()=>removeAllegato(i)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",padding:"0 0 0 4px",flexShrink:0}}>
                        <Trash2 size={11}/>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {preview && (
            <div style={{border:"1px solid #e5e7eb",borderRadius:8,overflow:"hidden",marginTop:10}}>
              <div style={{background:"#faf5ff",padding:"7px 10px",borderBottom:"1px solid #e9d5ff",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontSize:11,fontWeight:700,color:"#7c3aed",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{preview.tipo==="foto"?"🖼️":"📄"} {preview.nome}</span>
                <div style={{display:"flex",gap:8,flexShrink:0}}>
                  <a href={preview.url} target="_blank" rel="noreferrer" style={{fontSize:10,color:"#7c3aed",textDecoration:"none",fontWeight:600,display:"flex",alignItems:"center",gap:3}}><Eye size={11}/> Apri</a>
                  <a href={preview.url} download={preview.nome} style={{fontSize:10,color:"#059669",textDecoration:"none",fontWeight:600,display:"flex",alignItems:"center",gap:3}}><Download size={11}/> Scarica</a>
                  <button type="button" onClick={()=>setPreview(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:0}}><X size={12}/></button>
                </div>
              </div>
              {preview.tipo==="foto" ? (
                <img src={preview.url} alt={preview.nome} style={{width:"100%",maxHeight:280,objectFit:"contain",background:"#111827"}}/>
              ) : (
                <iframe src={preview.url} style={{width:"100%",height:280,border:"none"}} title={preview.nome}/>
              )}
            </div>
          )}
        </div>

        </fieldset>

        {/* Esito della chiusura e riaperture (W409) */}
        {seg?.stato==="chiusa" && seg.esito && (
          <div style={{marginTop:14,background:"#ecfdf5",border:"1px solid #a7f3d0",borderRadius:9,padding:"10px 12px"}}>
            <div style={{fontSize:11,fontWeight:800,color:"#065f46",marginBottom:4}}>✅ ESITO{seg.chiusaDaNome ? ` · chiusa da ${seg.chiusaDaNome}` : ""}{seg.chiusaData?.toDate ? ` il ${seg.chiusaData.toDate().toLocaleDateString("it-IT")}` : ""}</div>
            <div style={{fontSize:13,color:"#064e3b",whiteSpace:"pre-wrap"}}>{seg.esito}</div>
          </div>
        )}
        {(seg?.riaperture||[]).length>0 && (
          <div style={{marginTop:10,fontSize:11.5,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:9,padding:"8px 12px"}}>
            {seg.riaperture.map((r,i)=>(
              <div key={i}>↩️ Riaperta da <b>{r.daNome}</b>{r.il ? ` il ${new Date(r.il).toLocaleDateString("it-IT")}` : ""}: {r.motivo}{r.esitoPrecedente ? <span style={{color:"#a16207"}}> (esito precedente: {r.esitoPrecedente})</span> : null}</div>
            ))}
          </div>
        )}
        {azione && (
          <div style={{marginTop:14,border:`1.5px solid ${azione==="chiudi"?"#059669":"#d97706"}`,borderRadius:10,padding:"10px 12px",background:azione==="chiudi"?"#f0fdf4":"#fffbeb"}}>
            <label style={{...lbl,color:azione==="chiudi"?"#065f46":"#92400e",fontWeight:800}}>{azione==="chiudi" ? "ESITO * — cosa è stato fatto o deciso" : "PERCHÉ LA RIAPRI? *"}</label>
            <textarea autoFocus aria-label={azione==="chiudi" ? "Esito" : "Motivo della riapertura"} style={{...inp,minHeight:70,resize:"vertical"}} value={testoAzione} onChange={e=>setTestoAzione(e.target.value)}
              placeholder={azione==="chiudi" ? "Es. Sostituito l'interruttore differenziale del quadro piano 2, impianto verificato con il cliente" : "Es. Il cliente richiama: il problema si è ripresentato"}/>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
              <button type="button" className="btn btn-secondary" onClick={()=>{setAzione(null);setTestoAzione("");}}>Annulla</button>
              <button type="button" className="btn" disabled={!testoValido(testoAzione)} onClick={confermaAzione}
                style={{background:azione==="chiudi"?"#059669":"#d97706",color:"#fff",border:"none",opacity:testoValido(testoAzione)?1:.5}}>
                {azione==="chiudi" ? "✅ Chiudi la segnalazione" : "↩️ Riapri"}
              </button>
            </div>
            {!testoValido(testoAzione) && <div style={{fontSize:10.5,color:"#6b7280",marginTop:4}}>Almeno {MIN_ESITO} caratteri: resta scritto nella segnalazione e chi l'ha registrata riceve un avviso.</div>}
          </div>
        )}

        {seg?.id && cronologia.length>0 && (
          <div style={{marginTop:16,borderTop:"1px solid #f3f4f6",paddingTop:12}}>
            <div style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"flex",alignItems:"center",gap:5,marginBottom:8}}>
              <Clock size={12}/> Cronologia delle azioni
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:160,overflowY:"auto"}}>
              {cronologia.map(c=>{
                const ts = c.createdAt?.toDate?.();
                return (
                  <div key={c.id} style={{fontSize:11,color:"#374151",padding:"6px 8px",background:"#f9fafb",borderRadius:6}}>
                    <span style={{fontWeight:700,color:"#7c3aed"}}>{c.nomeUtente||"—"}</span>
                    {" "}
                    {c.campo==="creazione" ? <>ha creato la segnalazione</> :
                     c.campo==="presaInCarico" ? <>ha preso in carico la segnalazione</> :
                     c.campo==="chiusura" ? <>ha chiuso la segnalazione: <i>{c.after}</i></> :
                     c.campo==="riapertura" ? <>ha riaperto la segnalazione: <i>{c.after}</i></> :
                     <>ha modificato <strong>{c.campoLabel}</strong>: {c.before} → {c.after}</>}
                    {ts && <span style={{color:"#9ca3af",marginLeft:6}}>· {ts.toLocaleDateString("it-IT")} {ts.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:8,justifyContent:"space-between",marginTop:16}}>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            {seg?.id && (
              <button type="button" onClick={stampaPDF}
                style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",display:"flex",alignItems:"center",gap:5,fontSize:12,fontFamily:"'Inter',sans-serif",fontWeight:600}}>
                🖨️ Stampa PDF
              </button>
            )}
            {seg?.id && puoModificareContenuto && (
              <button type="button" onClick={async ()=>{
                if(!await confirm("Eliminare questa segnalazione?",{type:"danger"})) return;
                await deleteDoc(doc(db,"segnalazioniCliente",seg.id));
                addToast("Eliminata","info");
                onClose();
              }} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5",display:"flex",alignItems:"center",gap:5,fontSize:12,fontFamily:"'Inter',sans-serif"}}>
                <Trash2 size={13}/> Elimina
              </button>
            )}
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>{puoGestire && seg?.stato!=="chiusa"?"Annulla":"Chiudi"}</button>
            {azioni.prendi && !azione && (
              <button type="button" className="btn" onClick={prendiInCarico}
                style={{background:"#7c3aed",color:"#fff",border:"none"}}>
                ✋ Prendi in carico
              </button>
            )}
            {azioni.chiudi && !azione && (
              <button type="button" className="btn" onClick={()=>{setAzione("chiudi");setTestoAzione("");}}
                style={{background:"#059669",color:"#fff",border:"none"}}>
                ✅ Chiudi…
              </button>
            )}
            {azioni.riapri && !azione && (
              <button type="button" className="btn" onClick={()=>{setAzione("riapri");setTestoAzione("");}}
                style={{background:"#fff",color:"#b45309",border:"1.5px solid #f59e0b"}}>
                ↩️ Riapri…
              </button>
            )}
            {puoGestire && seg?.stato!=="chiusa" && (
              <button type="button" className="btn btn-primary" disabled={saving} onClick={()=>bozza.salvaCon(salva)}>
                {saving?"Salvataggio...":(seg?"💾 Salva modifiche":"💾 Registra segnalazione")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pagina Personale: solo le proprie segnalazioni ──────────────────────────
export default function SegnalazioneClientePage({ onNavigate, deepLink }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canAdmin = isOrgAdmin || isPlatformOwner;

  const [segnalazioni, setSegnalazioni] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editSeg, setEditSeg] = useState(null);

  useEffect(()=>{
    if(!effectiveOrgId||!currentUser) return;
    let mie=[], assegnate=[];
    const merge = () => {
      const m={}; [...mie,...assegnate].forEach(s=>{ m[s.id]=s; }); // dedup se sei sia compilatore che assegnatario
      setSegnalazioni(Object.values(m).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)));
    };
    const u1 = onSnapshot(
      query(collection(db,"segnalazioniCliente"),where("orgId","==",effectiveOrgId),where("compilatoreUid","==",currentUser.uid)),
      snap=>{ mie=snap.docs.map(d=>({id:d.id,...d.data()})); merge(); });
    const u2 = onSnapshot(
      query(collection(db,"segnalazioniCliente"),where("orgId","==",effectiveOrgId),where("assegnatarioUid","==",currentUser.uid)),
      snap=>{ assegnate=snap.docs.map(d=>({id:d.id,...d.data()})); merge(); });
    return ()=>{u1();u2();};
  },[effectiveOrgId,currentUser]);

  useEffect(()=>{
    if(!effectiveOrgId) return;
    onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),
      snap=>{ const l=snap.docs.map(d=>d.data()); l.sort((a,b)=>(a.displayName||"").localeCompare(b.displayName||"","it")); setUtenti(l); });
    onSnapshot(query(collection(db,"clienti"),where("organizationId","==",effectiveOrgId)),
      snap=>setClienti(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.ragioneSociale||"").localeCompare(b.ragioneSociale||"","it"))));
  },[effectiveOrgId]);

  // Da un avviso: si apre direttamente la segnalazione (W409)
  const apertaDaLink = useRef(null);
  useEffect(() => {
    const id = deepLink?.segId;
    if (!id || apertaDaLink.current === id || !effectiveOrgId) return;
    const trovata = segnalazioni.find(x => x.id === id);
    if (trovata) { apertaDaLink.current = id; setEditSeg(trovata); return; }
    getDoc(doc(db,"segnalazioniCliente",id)).then(d => { if (d.exists() && apertaDaLink.current !== id) { apertaDaLink.current = id; setEditSeg({ id: d.id, ...d.data() }); } }).catch(() => {});
  }, [deepLink?.segId, segnalazioni, effectiveOrgId]);

  // Commesse recenti: dalle proprie ultime segnalazioni già caricate, nessuna nuova chiamata a Firestore
  const commesseRecenti = useMemo(()=>{
    const viste = new Set(); const elenco = [];
    for(const s of segnalazioni){
      if(!s.projectId||viste.has(s.projectId)) continue;
      viste.add(s.projectId);
      elenco.push({ id:s.projectId, name:s.projectName, codiceCommessa:s.codiceCommessa });
      if(elenco.length>=5) break;
    }
    return elenco;
  },[segnalazioni]);

  return (
    <div style={{padding:24,maxWidth:920,flex:1,overflowY:"auto"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4,flexWrap:"wrap"}}>
        <h2 style={{fontSize:18,fontWeight:800,display:"flex",alignItems:"center",gap:8,margin:0}}>
          <Megaphone size={18} color="#7c3aed"/> Segnalazioni Cliente
          <InsightButton onNavigate={onNavigate}/>
        </h2>
        <button onClick={()=>setShowForm(true)} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:5,marginLeft:"auto"}}>
          <Plus size={14}/> Nuova Segnalazione
        </button>
      </div>
<AvvisoViola style={{marginBottom:14}}>
        <b>A cosa servono le Segnalazioni Cliente.</b> Qui si registrano le richieste del cliente <b>dopo la consegna</b> o su un impianto già funzionante: un guasto, un problema dell'impianto, una richiesta d'intervento o di manutenzione.
        Quello che il cliente segnala <b>mentre la commessa è in esecuzione</b> (osservazioni sui lavori, reclami, richieste di modifica) va registrato in <b>Corrispondenza</b>, dentro la commessa: così compare nella Relazione della commessa e nella Cabina di Regia.
      </AvvisoViola>
      <p style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>
        Registra rapidamente una richiesta ricevuta da un cliente (telefono, e-mail, WhatsApp, sopralluogo...). Vedi qui le segnalazioni che hai inserito tu e quelle assegnate a te — la vista completa per tutta l'azienda è nel Registro Segnalazioni Cliente.
      </p>

      {segnalazioni.length===0 ? (
        <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
          <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📣</div>
          <div style={{fontSize:14,fontWeight:600}}>Nessuna segnalazione registrata</div>
          <div style={{fontSize:12,marginTop:4}}>Registra la prima richiesta ricevuta da un cliente</div>
        </div>
      ) : (
        <div>
          {segnalazioni.map(s=>{
            const stato = STATI_SEG[s.stato]||STATI_SEG.nuova;
            return (
              <div key={s.id} onClick={()=>setEditSeg(s)}
                style={{border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:10,background:"#fff",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                <div style={{fontFamily:"monospace",fontWeight:800,color:"#7c3aed",fontSize:13,flexShrink:0}}>{s.numero||"—"}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.oggetto || s.descrizione}</div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                    {s.dataRicezione?.split("-").reverse().join("/")} · {s.canale} · <b style={{color:"#374151"}}>{s.clienteNome}</b>
                    {s.projectName ? <span style={{color:"#7c3aed",fontWeight:600}}> · {etichettaCommessa(s)}</span> : s.nessunaCommessa ? <span> · nessuna commessa</span> : null}
                    {s.assegnatarioNome && <span style={{marginLeft:8,color:"#d97706"}}>→ {s.assegnatarioNome}</span>}
                  </div>
                </div>
                <span style={{fontSize:11,padding:"3px 10px",borderRadius:10,background:stato.bg,color:stato.col,fontWeight:700,flexShrink:0}}>{stato.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {(showForm||editSeg) && (
        <SegnalazioneForm seg={editSeg} orgId={effectiveOrgId} currentUser={currentUser}
          utenti={utenti} clienti={clienti} commesseRecenti={commesseRecenti} canAdmin={canAdmin}
          onClose={()=>{setShowForm(false);setEditSeg(null);}}/>
      )}
    </div>
  );
}
